import React from 'react';
import { Link } from 'react-router-dom';
import './ErrorFallback.css';

interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  type?: 'page' | 'component' | 'network';
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ 
  error, 
  resetError, 
  type = 'component' 
}) => {
  const getErrorContent = () => {
    switch (type) {
      case 'page':
        return {
          title: 'Page Not Available',
          description: 'This page is temporarily unavailable. Please try again later or return to the homepage.',
          showHomeButton: true,
        };
      case 'network':
        return {
          title: 'Connection Error',
          description: 'Unable to load content. Please check your internet connection and try again.',
          showRetryButton: true,
        };
      default:
        return {
          title: 'Component Error',
          description: 'This section failed to load. The rest of the page should work normally.',
          showRetryButton: true,
        };
    }
  };

  const content = getErrorContent();

  return (
    <div className={`error-fallback error-fallback-${type}`}>
      <div className="error-fallback-content">
        <div className="error-fallback-icon">
          {type === 'network' ? (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M1 9L12 2L23 9V20C23 20.5304 22.7893 21.0391 22.4142 21.4142C22.0391 21.7893 21.5304 22 21 22H3C2.46957 22 1.96086 21.7893 1.58579 21.4142C1.21071 21.0391 1 20.5304 1 20V9Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2"/>
            </svg>
          ) : (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
              <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
            </svg>
          )}
        </div>

        <h3 className="error-fallback-title">{content.title}</h3>
        <p className="error-fallback-description">{content.description}</p>

        <div className="error-fallback-actions">
          {content.showRetryButton && resetError && (
            <button 
              className="error-fallback-button error-fallback-button-primary"
              onClick={resetError}
            >
              Try Again
            </button>
          )}
          
          {content.showHomeButton && (
            <Link 
              to="/"
              className="error-fallback-button error-fallback-button-secondary"
            >
              Go Home
            </Link>
          )}
        </div>

        {import.meta.env.DEV && error && (
          <details className="error-fallback-details">
            <summary>Error Details</summary>
            <pre className="error-fallback-stack">{error.message}</pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default ErrorFallback;
